import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SaleEntry from './pages/SaleEntry';
import SalesList from './pages/SalesList';
import SaleDetail from './pages/SaleDetail';
import Payments from './pages/Payments';
import Inventory from './pages/Inventory';
import Schedule from './pages/Schedule';
import ColorMatch from './pages/ColorMatch';
import UsersPage from './pages/UsersPage';
import Commissions from './pages/Commissions';
import Service from './pages/Service';
import InventoryPay from './pages/InventoryPay';
import InstallPay from './pages/InstallPay';

// Usernames (lowercase) that get manager-level access (Service + Schedule) even as salesmen
const MANAGER_WHITELIST = ['sara'];
// Usernames (lowercase) locked to the Inventory page only
const INVENTORY_ONLY_WHITELIST = ['jiwan', 'kushdeep'];
// Usernames (lowercase) locked to the Install Pay page only (installers)
const INSTALLER_ONLY_WHITELIST = ['miguel'];

export function hasManagerAccess(user) {
  if (!user) return false;
  if (user.role === 'admin' || user.role === 'manager') return true;
  return MANAGER_WHITELIST.includes(user.username?.toLowerCase());
}
// Legacy alias
export const canAccessService = hasManagerAccess;

export function isInventoryOnly(user) {
  if (!user) return false;
  return INVENTORY_ONLY_WHITELIST.includes(user.username?.toLowerCase());
}

export function isInstallerOnly(user) {
  if (!user) return false;
  return INSTALLER_ONLY_WHITELIST.includes(user.username?.toLowerCase());
}

export function canEditInventory(user) {
  if (!user) return false;
  return user.role === 'admin' || user.role === 'manager' || isInventoryOnly(user);
}

// Picks the correct "My Pay" view based on what the user earns
function MyPayDispatch() {
  const { user } = useAuth();
  if (isInstallerOnly(user)) return <InstallPay />;
  return <InventoryPay />;
}

function ProtectedRoute({ children, adminOnly, check, path }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  // Inventory-only users can access /inventory and /my-pay only
  if (isInventoryOnly(user) && path !== '/inventory' && path !== '/my-pay') return <Navigate to="/inventory" />;
  // Installer-only users can access /my-pay only
  if (isInstallerOnly(user) && path !== '/my-pay') return <Navigate to="/my-pay" />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" />;
  if (check && !check(user)) return <Navigate to="/" />;
  return <Layout>{children}</Layout>;
}

export default function App() {
  const { user, loading } = useAuth();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading...</div>;

  const homePath = isInventoryOnly(user) ? '/inventory'
                 : isInstallerOnly(user) ? '/my-pay'
                 : '/';

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={homePath} /> : <Login />} />
      <Route path="/" element={<ProtectedRoute path="/"><Dashboard /></ProtectedRoute>} />
      <Route path="/sales/new" element={<ProtectedRoute path="/sales/new"><SaleEntry /></ProtectedRoute>} />
      <Route path="/sales/:id" element={<ProtectedRoute path="/sales/:id"><SaleDetail /></ProtectedRoute>} />
      <Route path="/sales" element={<ProtectedRoute path="/sales" check={hasManagerAccess}><SalesList /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute path="/payments" check={hasManagerAccess}><Payments /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute path="/inventory" check={canEditInventory}><Inventory /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute path="/schedule" check={hasManagerAccess}><Schedule /></ProtectedRoute>} />
      <Route path="/color-match" element={<ProtectedRoute path="/color-match" adminOnly><ColorMatch /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute path="/users" adminOnly><UsersPage /></ProtectedRoute>} />
      <Route path="/commissions" element={<ProtectedRoute path="/commissions"><Commissions /></ProtectedRoute>} />
      <Route path="/service" element={<ProtectedRoute path="/service" check={canAccessService}><Service /></ProtectedRoute>} />
      <Route path="/my-pay" element={<ProtectedRoute path="/my-pay"><MyPayDispatch /></ProtectedRoute>} />
      <Route path="/inventory-pay/:userId" element={<ProtectedRoute path="/inventory-pay/:userId" check={hasManagerAccess}><InventoryPay /></ProtectedRoute>} />
      <Route path="/install-pay/:userId" element={<ProtectedRoute path="/install-pay/:userId" check={hasManagerAccess}><InstallPay /></ProtectedRoute>} />
    </Routes>
  );
}
